import React from 'react';
import {
  Shuffle,
  Layers3,
  Sparkles,
  FileText,
  Puzzle,
  Hash,
  Type,
  Check,
  ChevronDown,
} from 'lucide-react';
import Popover from '../common/Popover';

export const PRIMARY_MODES = [
  {
    value: 'auto',
    label: 'Авто',
    previous: '',
    hint: 'Сам выбирает подходящий маршрут: код, бренд, описание или артикул',
    Icon: Shuffle,
    recommended: true,
  },
  {
    value: 'fulltext',
    label: 'Слова в описании',
    previous: 'ранее Текст',
    hint: 'Ищет слова из запроса в тексте описания',
    Icon: FileText,
  },
  {
    value: 'ilike',
    label: 'Подстрока в описании',
    previous: 'ранее Подстрока',
    hint: 'Ищет введённую последовательность символов в описании без смыслового расширения',
    Icon: Type,
  },
  {
    value: 'semantic',
    label: 'Похожие товары',
    previous: 'ранее Смысл',
    hint: 'Ищет декларации с похожим по смыслу описанием товара',
    Icon: Sparkles,
  },
  {
    value: 'exact',
    label: 'Код УКТЗЕД',
    previous: 'ранее Код',
    hint: 'Показывает быстрые примеры внутри кода или префикса',
    Icon: Hash,
  },
];

export const ADVANCED_MODES = [
  {
    value: 'hybrid',
    label: 'Глубокий поиск',
    previous: 'ранее Гибрид',
    hint: 'Подключает несколько способов поиска, если Авто пропустил важные варианты. Может работать дольше.',
    Icon: Layers3,
  },
  {
    value: 'trigram',
    label: 'Похожее написание',
    previous: 'ранее Опечатка',
    hint: 'Для опечаток в бренде, похожих моделей и артикулов с другими разделителями',
    Icon: Puzzle,
  },
];

export const SEARCH_MODES = [...PRIMARY_MODES, ...ADVANCED_MODES];

export function getMode(value) {
  return SEARCH_MODES.find(m => m.value === value) || SEARCH_MODES[0];
}

export function ModeTrigger({ mode, open, onToggle }) {
  const meta = getMode(mode);
  const Icon = meta.Icon;
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-haspopup="listbox"
      aria-expanded={open}
      title={meta.hint}
      className="flex h-7 items-center gap-1.5 rounded-md px-2 text-xs font-medium text-text-secondary hover:bg-hover transition-colors max-w-[180px]"
    >
      <Icon className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400 shrink-0" />
      <span className="truncate">{meta.label}</span>
      <ChevronDown className={`w-3 h-3 text-text-disabled transition-transform shrink-0 ${open ? 'rotate-180' : ''}`} />
    </button>
  );
}

function ModeOption({ m, active, onSelect }) {
  const Icon = m.Icon;
  return (
    <button
      type="button"
      onClick={() => onSelect(m.value)}
      className={`flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors hover:bg-hover ${
        active
          ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300'
          : 'text-text-primary'
      }`}
    >
      <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center text-text-muted">
        <Icon className="w-3.5 h-3.5" />
      </span>
      <span className="flex-1 min-w-0">
        <span className="flex items-center gap-1.5 flex-wrap">
          <span className="font-medium">{m.label}</span>
          {m.recommended && (
            <span className="rounded bg-blue-600/10 px-1 text-[9px] font-semibold uppercase tracking-wider text-blue-700 dark:text-blue-300">
              рек.
            </span>
          )}
          {m.previous && (
            <span className="text-[10px] text-text-disabled">{m.previous}</span>
          )}
        </span>
        <span className="block text-[11px] text-text-muted leading-snug">{m.hint}</span>
      </span>
      {active && <Check className="mt-0.5 w-3.5 h-3.5 text-blue-600 dark:text-blue-400 shrink-0" />}
    </button>
  );
}

export default function ModePopover({ open, onClose, mode, onChange }) {
  const handleSelect = (value) => {
    onChange(value);
    onClose?.();
  };

  return (
    <Popover open={open} onClose={onClose} align="end" className="w-[340px] p-1.5">
      <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-wider text-text-muted">
        Способ поиска
      </div>
      {PRIMARY_MODES.map(m => (
        <ModeOption key={m.value} m={m} active={mode === m.value} onSelect={handleSelect} />
      ))}

      <div className="my-1.5 border-t border-border-default" />

      <div className="px-2 pt-1 pb-1 text-[10px] uppercase tracking-wider text-text-muted">
        Расширенные
      </div>
      {ADVANCED_MODES.map(m => (
        <ModeOption key={m.value} m={m} active={mode === m.value} onSelect={handleSelect} />
      ))}
    </Popover>
  );
}
